import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowsOutSimple, ChatCircleDots, CircleNotch, PaperPlaneTilt, Sparkle, X } from '@phosphor-icons/react'
import { Button, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils'
import { requestSupportAnswer } from '../api'
import { createChatId } from '../utils'
import { useSidePanel } from '@/lib/context/sidePanelContext'
import type { ChatMessage } from '../api/types'

const formatTime = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value))

const PANEL_LIMITS = {
  MIN_WIDTH: 360,
  MAX_WIDTH: 900,
  MIN_HEIGHT: 360,
  MAX_HEIGHT: 900
}

const ChatWidgetComponent = () => {
  const { t } = useTranslation('common')
  const { isOpen: isSidePanelOpen } = useSidePanel()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: createChatId('assistant'),
      role: 'assistant',
      content: t('support_chat.initial_message'),
      createdAt: new Date().toISOString()
    }
  ])
  const [draft, setDraft] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [panelSize, setPanelSize] = useState({ width: 380, height: 520 })
  const floatingContainerRef = useRef<HTMLDivElement>(null)
  const conversationRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const previousContainerRectRef = useRef<DOMRect | null>(null)
  const positionAnimationRef = useRef<Animation | null>(null)
  const resizeSessionRef = useRef<{
    startX: number
    startY: number
    startWidth: number
    startHeight: number
  } | null>(null)

  useEffect(() => {
    if (!conversationRef.current) return
    conversationRef.current.scrollTo({ top: conversationRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, isOpen])

  useEffect(() => {
    if (!isOpen) return
    const timer = window.setTimeout(() => textareaRef.current?.focus(), 250)
    return () => window.clearTimeout(timer)
  }, [isOpen])

  const helperText = useMemo(() => (isSending ? t('support_chat.helper_typing') : t('support_chat.helper_idle')), [isSending, t])

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev)
    setErrorMessage(null)
  }, [])

  const handleResizePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!resizeSessionRef.current) return

      const { startX, startY, startWidth, startHeight } = resizeSessionRef.current
      const deltaX = event.clientX - startX
      const deltaY = event.clientY - startY
      const nextWidth = clampDimension(isSidePanelOpen ? startWidth + deltaX : startWidth - deltaX, PANEL_LIMITS.MIN_WIDTH, PANEL_LIMITS.MAX_WIDTH)
      const nextHeight = clampDimension(startHeight - deltaY, PANEL_LIMITS.MIN_HEIGHT, PANEL_LIMITS.MAX_HEIGHT)
      setPanelSize({ width: nextWidth, height: nextHeight })
    },
    [isSidePanelOpen]
  )

  const handleResizePointerUp = useCallback(() => {
    if (typeof window === 'undefined') return
    window.removeEventListener('pointermove', handleResizePointerMove)
    window.removeEventListener('pointerup', handleResizePointerUp)
    resizeSessionRef.current = null
  }, [handleResizePointerMove])

  const handleResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return
      event.preventDefault()
      event.stopPropagation()

      resizeSessionRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        startWidth: panelSize.width,
        startHeight: panelSize.height
      }

      if (typeof window === 'undefined') return
      window.addEventListener('pointermove', handleResizePointerMove)
      window.addEventListener('pointerup', handleResizePointerUp)
    },
    [handleResizePointerMove, handleResizePointerUp, panelSize.height, panelSize.width]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    return () => {
      window.removeEventListener('pointermove', handleResizePointerMove)
      window.removeEventListener('pointerup', handleResizePointerUp)
    }
  }, [handleResizePointerMove, handleResizePointerUp])

  useLayoutEffect(() => {
    const container = floatingContainerRef.current
    if (!container) return

    const nextRect = container.getBoundingClientRect()
    const previousRect = previousContainerRectRef.current

    if (previousRect) {
      const deltaX = previousRect.left - nextRect.left

      if (deltaX !== 0) {
        positionAnimationRef.current?.cancel()
        positionAnimationRef.current = container.animate([{ transform: `translateX(${deltaX}px)` }, { transform: 'translate(0px, 0px)' }], {
          duration: 320,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both'
        })
      }
    }

    previousContainerRectRef.current = nextRect
  }, [isSidePanelOpen])

  useEffect(() => () => positionAnimationRef.current?.cancel(), [])

  const sendDraftMessage = useCallback(async () => {
    const trimmed = draft.trim()
    if (!trimmed || isSending) return

    const userMessage: ChatMessage = {
      id: createChatId('user'),
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString()
    }

    const assistantMessageId = createChatId('assistant-temp')

    const assistantPlaceholder: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString()
    }

    const conversationForRequest = [...messages, userMessage]
    const optimisticConversation = [...conversationForRequest, assistantPlaceholder]

    setMessages(optimisticConversation)
    setDraft('')
    setIsSending(true)
    setErrorMessage(null)

    try {
      const assistantReply = await requestSupportAnswer({ message: trimmed, messages: conversationForRequest }, chunk => {
        setMessages(prev =>
          prev.map(message => (message.id === assistantMessageId ? { ...message, content: `${message.content}${chunk}` } : message))
        )
      })

      setMessages(prev =>
        prev.map(message =>
          message.id === assistantMessageId
            ? {
                ...assistantReply,
                id: assistantReply.id ?? assistantMessageId,
                createdAt: assistantReply.createdAt ?? new Date().toISOString()
              }
            : message
        )
      )
    } catch (error) {
      console.error('Support chat failed', error)
      setErrorMessage(t('support_chat.error.generic'))
      setMessages(prev =>
        prev.map(message => (message.id === assistantMessageId ? { ...message, role: 'system', content: t('support_chat.error.generic') } : message))
      )
    } finally {
      setIsSending(false)
    }
  }, [draft, isSending, messages, t])

  const handleComposerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || event.shiftKey) return
      const hasText = draft.trim().length > 0
      if (!hasText || isSending) return

      event.preventDefault()
      sendDraftMessage()
    },
    [draft, isSending, sendDraftMessage]
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      await sendDraftMessage()
    },
    [sendDraftMessage]
  )

  return (
    <div
      ref={floatingContainerRef}
      className={cn(
        'pointer-events-none fixed bottom-6 z-50 flex flex-col gap-3 transition-all duration-300 sm:bottom-8 lg:bottom-10',
        isSidePanelOpen ? 'items-start' : 'items-end',
        isSidePanelOpen ? 'left-4 right-auto sm:left-8 lg:left-10' : 'left-auto right-4 sm:right-8 lg:right-10'
      )}
    >
      <div className={cn('pointer-events-auto flex flex-col gap-3', isSidePanelOpen ? 'items-start' : 'items-end')}>
        {isOpen && (
          <section
            id='valueize-chat-panel'
            role='dialog'
            aria-label={t('support_chat.aria_label')}
            className='relative flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-white text-gray-900 shadow-[0_25px_60px_-20px_rgba(15,23,42,0.55)] transition-all duration-300'
            style={{ width: panelSize.width, height: panelSize.height, maxWidth: '92vw', maxHeight: '85vh' }}
          >
            <header className='relative flex items-start justify-between gap-3 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 px-4 py-4 text-white'>
              <div className='ml-5'>
                <p className='text-sm font-semibold tracking-wide'>{t('support_chat.title')}</p>
                <div className='-ml-[2px] mt-2 flex items-center gap-2 text-xs text-white/70'>
                  <Sparkle size={16} weight='fill' className='text-yellow-300' />
                  <span>{t('support_chat.tagline')}</span>
                </div>
              </div>
              <Button
                type='button'
                size='sm'
                variant='ghost'
                onClick={handleToggle}
                aria-label={t('support_chat.actions.close')}
                className={cn(
                  'absolute top-2 h-6 w-6 min-w-0 rounded-full bg-white/10 p-0 text-white transition hover:bg-white/20',
                  isSidePanelOpen ? 'left-2 right-auto' : 'left-auto right-2'
                )}
              >
                <X size={14} weight='bold' />
              </Button>
            </header>
            <div className='flex-1 overflow-y-auto bg-gradient-to-b from-white to-gray-50 px-4 py-5' ref={conversationRef} aria-live='polite'>
              {messages.map(message => (
                <article key={message.id} className={cn('mb-4 flex', message.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm',
                      message.role === 'user' && 'bg-gray-900 text-white',
                      message.role === 'assistant' && 'bg-white text-gray-900 ring-1 ring-gray-100',
                      message.role === 'system' && 'bg-amber-50 text-amber-900'
                    )}
                  >
                    <MessageRichText content={message.content} />
                    <span className='mt-2 block text-[10px] font-semibold uppercase tracking-wide opacity-60'>{formatTime(message.createdAt)}</span>
                  </div>
                </article>
              ))}
              {isSending && (
                <div className='mb-2 flex justify-start'>
                  <div className='flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-xs text-gray-500 ring-1 ring-gray-200'>
                    <CircleNotch size={16} className='text-gray-400 animate-spin' />
                    {t('support_chat.typing_indicator')}
                  </div>
                </div>
              )}
            </div>
            <form onSubmit={handleSubmit} className='border-t border-gray-100 px-4 pb-4 pt-3'>
              <Textarea
                ref={textareaRef}
                rows={3}
                placeholder={t('support_chat.input_placeholder')}
                value={draft}
                onChange={event => setDraft(event.target.value)}
                onKeyDown={handleComposerKeyDown}
                disabled={isSending}
              />
              <div className='mt-3 flex items-center justify-between text-xs text-gray-500'>
                <span>{helperText}</span>
                <Button type='submit' size='md' variant='primary' disabled={!draft.trim() || isSending} className='gap-2 rounded-2xl'>
                  <PaperPlaneTilt size={16} weight='fill' />
                  {t('buttons.send')}
                </Button>
              </div>
              {errorMessage && <p className='text-red-500 mt-2 text-xs font-medium'>{errorMessage}</p>}
            </form>
            <Button
              type='button'
              size='sm'
              variant='ghost'
              aria-label={t('support_chat.actions.resize', 'Resize chat panel')}
              onPointerDown={handleResizePointerDown}
              className={cn(
                'absolute top-2 flex h-5 w-5 min-w-0 items-center justify-center rounded-full border border-gray-200 bg-white p-0 text-gray-500 shadow-sm transition hover:bg-gray-50',
                isSidePanelOpen ? 'left-auto right-2 cursor-nesw-resize' : 'left-2 right-auto cursor-nwse-resize'
              )}
            >
              <ArrowsOutSimple size={10} weight='bold' className={cn(isSidePanelOpen ? 'rotate-0' : 'rotate-90', 'transform')} />
            </Button>
          </section>
        )}
        <div className={cn('flex flex-col gap-2', isSidePanelOpen ? 'items-start' : 'items-end')}>
          {!isOpen && (
            <div className='rounded-2xl border border-white/50 bg-white/90 px-4 py-2 text-sm font-semibold text-gray-900 shadow-lg backdrop-blur'>
              {t('support_chat.tooltip_closed')}
            </div>
          )}
          <Button
            type='button'
            size='lg'
            variant={isOpen ? 'ghost' : 'primary'}
            aria-expanded={isOpen}
            aria-controls='valueize-chat-panel'
            onClick={handleToggle}
            className={cn(
              'gap-2 rounded-full border border-transparent shadow-xl transition-all duration-200',
              isOpen ? 'bg-white text-gray-900 hover:bg-white/90' : 'bg-gray-900 text-white hover:bg-gray-800'
            )}
          >
            <ChatCircleDots size={18} weight='fill' />
            {isOpen ? t('support_chat.cta_close') : t('support_chat.cta_open')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export const ChatWidget = memo(ChatWidgetComponent)

type ParagraphBlock = {
  id: string
  type: 'paragraph'
  content: string
}

type ListItem = {
  id: string
  content: string
}

type ListBlock = {
  id: string
  type: 'list'
  ordered: boolean
  start?: number
  items: ListItem[]
}

type MessageBlock = ParagraphBlock | ListBlock

const MessageRichText = memo(({ content }: { content: string }) => {
  const blocks = useMemo(() => buildMessageBlocks(content), [content])

  return (
    <div className='flex flex-col gap-2 break-words text-sm leading-relaxed'>
      {blocks.map(block => {
        const blockKey = block.id
        if (block.type === 'paragraph') {
          return (
            <p key={blockKey} className='whitespace-pre-wrap'>
              {renderInlineSegments(block.content)}
            </p>
          )
        }

        if (block.ordered) {
          return (
            <ol key={blockKey} start={block.start} className='ml-5 list-decimal space-y-1 text-sm marker:font-semibold'>
              {block.items.map(item => (
                <li key={item.id} className='pl-1'>
                  {renderInlineSegments(item.content)}
                </li>
              ))}
            </ol>
          )
        }

        return (
          <ul key={blockKey} className='ml-5 list-disc space-y-1 text-sm'>
            {block.items.map(item => (
              <li key={item.id} className='pl-1'>
                {renderInlineSegments(item.content)}
              </li>
            ))}
          </ul>
        )
      })}
    </div>
  )
})

const clampDimension = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const buildMessageBlocks = (content: string): MessageBlock[] => {
  const blocks: MessageBlock[] = []
  const lines = content.split(/\r?\n/)
  let paragraphBuffer: string[] = []
  let listBuffer: { ordered: boolean; start?: number; items: ListItem[] } | null = null
  let blockCounter = 0
  let listItemCounter = 0

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return
    blocks.push({ id: `paragraph-${blockCounter++}`, type: 'paragraph', content: paragraphBuffer.join('\n') })
    paragraphBuffer = []
  }

  const flushList = () => {
    if (!listBuffer) return
    blocks.push({ id: `list-${blockCounter++}`, type: 'list', ordered: listBuffer.ordered, start: listBuffer.start, items: listBuffer.items })
    listBuffer = null
  }

  lines.forEach(rawLine => {
    const line = rawLine.trim()
    if (!line) {
      flushParagraph()
      // blank lines between list items should not close the list
      return
    }

    const bulletMatch = line.match(/^([*-])\s+(.*)$/)
    const orderedMatch = line.match(/^(\d+)\.\s+(.*)$/)

    const orderedPayload = orderedMatch?.[2]?.trim()
    const looksLikeBoldHeading = Boolean(orderedPayload && orderedPayload.startsWith('**'))

    if (looksLikeBoldHeading && orderedPayload) {
      flushParagraph()
      flushList()
      paragraphBuffer.push(orderedPayload)
      return
    }

    if (bulletMatch || orderedMatch) {
      const isOrdered = Boolean(orderedMatch)
      const itemContent = bulletMatch ? bulletMatch[2] : orderedMatch![2]

      flushParagraph()

      if (!listBuffer || listBuffer.ordered !== isOrdered) {
        flushList()
        listBuffer = {
          ordered: isOrdered,
          start: isOrdered && orderedMatch ? Number.parseInt(orderedMatch[1], 10) : undefined,
          items: []
        }
      }

      listBuffer!.items.push({ id: `list-item-${listItemCounter++}`, content: itemContent })
      return
    }

    flushList()
    paragraphBuffer.push(line)
  })

  flushParagraph()
  flushList()

  if (!blocks.length) {
    return [{ id: 'paragraph-standalone', type: 'paragraph', content }]
  }

  return blocks
}

const renderInlineSegments = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  const boldRegex = /\*\*(.+?)\*\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null = boldRegex.exec(text)

  while (match) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    nodes.push(
      <strong key={`bold-${match.index}`} className='text-current font-semibold'>
        {match[1]}
      </strong>
    )

    lastIndex = match.index + match[0].length
    match = boldRegex.exec(text)
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  if (!nodes.length) {
    return [text]
  }

  return nodes
}
